import { CorporateDashboardContext } from "contexts/CorporateDashboardContext/CorporateDashboardContext";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { useContext, useMemo } from "react";
import ReportDowntimeBarChart from "components/ReportDowntimeBarChart";
import NoData from "components/NoData/NoData.view";
import { useStyles } from "../TopFiveFailureCodesAllMills/TopFiveFailureCodesAllMills.styles";
import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";
import { calculateTrailingTrend, percentageChecker } from "utilities/utilities";
import { CircularProgress } from "@mui/material";
import { SummaryOption, DisplayInOptionsEnum } from "constants/constants";
import { IMillReportCardProps } from "pages/Corporate/Locations/MillDashboards/MillDashboardInterfaces";
import { IDowntimeTotalsSummarized } from "../TotalDowntimeByMill/TotalDowntimeByMillInterfaces";
import CorporateMillBarComponent from "../CorporateMillBarComponent";

const DowntimeFailureCodeByMill = ({ countryCode }: IMillReportCardProps) => {
    const classes = useStyles();
    const { selectedFailureType, selectedFailureCode, filters, totalDowntimeSeconds } =
        useContext(CorporateDashboardContext);
    const { corporations } = useContext(CorporateContext);
    const corporateId = corporations[0]?.corporate_id || 0;
    const { downtimeSummaryAllMills, downtimeSummaryTrailingPeriod, loading } =
        CorporationsEntity.GetTotalDowntimeSummaryByCodeOrSecondaryCodeAllMillsCorporateDashboard(
            filters,
            SummaryOption.BY_CODE,
            corporateId,
            selectedFailureCode?.id,
        );

    const downtimeSummaryFormatted = useMemo(
        () =>
            downtimeSummaryAllMills.reduce((acc: IDowntimeTotalsSummarized[], obj) => {
                let millId = obj.mill.mill_id;
                const existingMillIndex = acc.findIndex((summary) => summary.millId === millId);

                if (!(existingMillIndex >= 0)) {
                    acc = [
                        ...acc,
                        {
                            millId: obj.mill.mill_id,
                            name: obj.mill.name,
                            typeTotals: [
                                {
                                    name: obj.failure_data.name,
                                    color_code: selectedFailureType?.color_code || "",
                                    total_seconds: obj.total_seconds,
                                    total_occurrences: obj.total_occurrences,
                                    default_failure_code_translations:
                                        obj.failure_data.default_failure_code_translations,
                                },
                            ],
                        },
                    ];
                } else {
                    const existingFailureTypeIndex = acc[existingMillIndex].typeTotals.findIndex(
                        (summary) => summary.name === obj.failure_data.name,
                    );

                    if (!(existingFailureTypeIndex >= 0)) {
                        acc[existingMillIndex] = {
                            ...acc[existingMillIndex],
                            typeTotals: [
                                ...acc[existingMillIndex].typeTotals,
                                {
                                    name: obj.failure_data.name,
                                    color_code: selectedFailureType?.color_code || "",
                                    total_seconds: obj.total_seconds,
                                    total_occurrences: obj.total_occurrences,
                                    default_failure_code_translations:
                                        obj.failure_data.default_failure_code_translations,
                                },
                            ],
                        };
                    } else {
                        acc[existingMillIndex].typeTotals[existingFailureTypeIndex] = {
                            ...acc[existingMillIndex].typeTotals[existingFailureTypeIndex],
                            total_seconds:
                                acc[existingMillIndex].typeTotals[existingFailureTypeIndex].total_seconds +
                                obj.total_seconds,
                            total_occurrences:
                                acc[existingMillIndex].typeTotals[existingFailureTypeIndex].total_occurrences +
                                obj.total_occurrences,
                        };
                    }
                }
                return acc;
            }, []),
        [downtimeSummaryAllMills, selectedFailureType?.color_code],
    );

    const graphData = useMemo(
        () =>
            downtimeSummaryFormatted
                .map((downtimeSummary) => {
                    let data: { [key: string]: string | number } = {
                        millId: downtimeSummary.millId,
                        mill: downtimeSummary.name,
                        countryCode: countryCode as string,
                        summaryOption: SummaryOption.BY_CODE,
                    };
                    downtimeSummary.typeTotals.forEach((total) => {
                        const secondsToMinutes =
                            Math.round(total.total_seconds / 60) < 1 ? 1 : Math.round(total.total_seconds / 60);
                        data = {
                            ...data,
                            [`${total.name}`]:
                                filters.displayIn === DisplayInOptionsEnum.MINUTES
                                    ? secondsToMinutes
                                    : filters.displayIn === DisplayInOptionsEnum.PERCENT
                                    ? totalDowntimeSeconds
                                        ? (100 * total.total_seconds) / totalDowntimeSeconds
                                        : 0
                                    : total.total_occurrences,
                            [`${total.name}Color`]: total.color_code,
                        };
                    });
                    const trailingMillResults = downtimeSummaryTrailingPeriod.filter(
                        (trailingSummary) => trailingSummary.mill.mill_id === downtimeSummary.millId,
                    );

                    const currentValue = Math.round(
                        downtimeSummary.typeTotals.reduce((acc, obj) => acc + obj.total_seconds, 0) /
                            downtimeSummary.typeTotals.reduce((acc, obj) => acc + obj.total_occurrences, 0),
                    );
                    const trailingValue =
                        trailingMillResults.length > 0
                            ? Math.round(
                                  trailingMillResults.reduce((acc, obj) => acc + obj.total_seconds, 0) /
                                      trailingMillResults.reduce((acc, obj) => acc + obj.total_occurrences, 0),
                              )
                            : 0;
                    const totalDowntime = downtimeSummary.typeTotals.reduce(
                        (acc, obj) =>
                            filters.displayIn === DisplayInOptionsEnum.MINUTES
                                ? (Math.round(obj.total_seconds / 60) < 1 ? 1 : Math.round(obj.total_seconds / 60)) +
                                  acc
                                : filters.displayIn === DisplayInOptionsEnum.PERCENT
                                ? (totalDowntimeSeconds ? (100 * obj.total_seconds) / totalDowntimeSeconds : 0) + acc
                                : obj.total_occurrences + acc,
                        0,
                    );
                    data = {
                        ...data,
                        totalDowntimeByMill:
                            filters.displayIn === DisplayInOptionsEnum.PERCENT
                                ? percentageChecker(totalDowntime)
                                : totalDowntime,
                        trendValue: calculateTrailingTrend(currentValue, trailingValue),
                    };

                    return data;
                })
                .sort((a, b) => Number(a.totalDowntimeByMill) - Number(b.totalDowntimeByMill)),
        [downtimeSummaryFormatted, totalDowntimeSeconds, downtimeSummaryTrailingPeriod, filters.displayIn, countryCode],
    );

    return loading ? (
        <div className={classes.noData}>
            <CircularProgress size={24} color="info" />
        </div>
    ) : graphData.length > 0 ? (
        <ReportDowntimeBarChart
            customBarComponent={CorporateMillBarComponent}
            data={graphData}
            index="mill"
            noPercent={filters.displayIn !== DisplayInOptionsEnum.PERCENT}
            noLegend
        />
    ) : (
        <NoData className={classes.noData} />
    );
};

export default DowntimeFailureCodeByMill;
